import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { RouterModule } from '@angular/router';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexFill,
  ApexGrid,
  ApexLegend,
  ApexMarkers,
  ApexNonAxisChartSeries,
  ApexPlotOptions,
  ApexResponsive,
  ApexStroke,
  ApexTitleSubtitle,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
  ChartComponent,
  NgApexchartsModule,
} from 'ng-apexcharts';
import { NgxPaginationModule } from 'ngx-pagination';
import { ToastrModule, ToastrService } from 'ngx-toastr';

import { PreloaderComponent } from '@app/modules/utils/preloader/preloader.component';
import { ApiClientService } from '@shared/_services/api-client.service';
import { cardData } from '../../../constants';

type DateCount = {
  date: string;
  count: number;
};

type DashboardTotals = {
  users: number;
  devices: number;
  themes: number;
  subscriptions: {
    free: number;
    paid: number;
    unknownTheme: number;
    total: number;
  };
  subscribedUsers: number;
};

type RevenueByTheme = {
  themeId: string;
  themeName: string;
  currency: string;
  totalAmount: number;
  transactionCount: number;
};

type ActiveTagsByTheme = {
  themeId: string;
  themeName: string;
  activeTagCount: number;
};

type DashboardAnalytics = {
  totals: DashboardTotals;
  revenueByTheme: RevenueByTheme[];
  revenueTotalsByCurrency: Record<string, number>;
  signupsByDate: DateCount[];
  themesCreatedByDate: DateCount[];
  activeTagsByTheme: ActiveTagsByTheme[];
  range: {
    from: string;
    to: string;
  };
};

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  colors: string[];
  stroke: ApexStroke;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  tooltip: ApexTooltip;
  legend: ApexLegend;
  grid: ApexGrid;
  markers: ApexMarkers;
  title: ApexTitleSubtitle;
};

export type ChartOptionsDonut = {
  series: ApexNonAxisChartSeries;
  chart: ApexChart;
  labels: string[];
  colors: string[];
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  responsive: ApexResponsive[];
  legend: ApexLegend;
  tooltip: ApexTooltip;
};

type SubscriptionMixItem = {
  label: string;
  shortLabel: string;
  value: number;
  percent: string;
  color: string;
};

const chartColors = {
  signups: '#148ff5',
  themes: '#19d29b',
  free: '#148ff5',
  paid: '#19d29b',
  unknown: '#f8a51b',
};

export type ChartOptionsBars = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  fill: ApexFill;
  markers: ApexMarkers;
  tooltip: ApexTooltip;
};

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    ToastrModule,
    NgxPaginationModule,
    NgApexchartsModule,
    RouterModule,
    PreloaderComponent,
  ],
  providers: [ToastrService],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.css',
})
export class DashboardComponent implements OnInit {
  public dashboardLoading = false;
  public usersLoading = false;
  public hasError = false;
  public errorMsg = '';
  public users: any[] = [];
  public totalItems = 0;
  public currentPage = 1;
  public pageSize = 5;
  public dashboardRangeDays = 30;

  public totalUser = 0;
  public totalDevices = 0;
  public totalSubscriptions = 0;
  public totalUserThemes = 0;
  public totalSubscribedUsers = 0;

  public subscriptionTotals: DashboardTotals['subscriptions'] = {
    free: 0,
    paid: 0,
    unknownTheme: 0,
    total: 0,
  };
  public revenueByTheme: RevenueByTheme[] = [];
  public revenueTotalsByCurrency: Array<{ currency: string; amount: number }> = [];
  public activeTagsByTheme: ActiveTagsByTheme[] = [];
  public dashboardRangeLabel = '';

  @ViewChild('chart') chart?: ChartComponent;
  public chartOptions!: ChartOptions;
  public chartOptionsDonut!: ChartOptionsDonut;
  public chartOptionsLines!: ChartOptionsBars;

  public cardData = cardData;
  public rangeOptions = [30, 60, 90];

  public get subscriptionMixItems(): SubscriptionMixItem[] {
    const total = this.subscriptionTotals.total || 0;
    const formatPercent = (value: number) => (total ? ((value / total) * 100).toFixed(1) : '0.0');

    return [
      {
        label: 'Free themes',
        shortLabel: 'Free',
        value: this.subscriptionTotals.free,
        percent: formatPercent(this.subscriptionTotals.free),
        color: chartColors.free,
      },
      {
        label: 'Paid themes',
        shortLabel: 'Paid',
        value: this.subscriptionTotals.paid,
        percent: formatPercent(this.subscriptionTotals.paid),
        color: chartColors.paid,
      },
      {
        label: 'Unknown theme',
        shortLabel: 'Unknown',
        value: this.subscriptionTotals.unknownTheme,
        percent: formatPercent(this.subscriptionTotals.unknownTheme),
        color: chartColors.unknown,
      },
    ];
  }

  constructor(
    private _apiClientService: ApiClientService,
    private _toastrService: ToastrService,
  ) {
    this.setEmptyCharts();
  }

  ngOnInit() {
    this.getAllUsers();
    this.getDashboardData();
  }

  public getValues(key: string): number {
    return Number((this as any)[key] || 0);
  }

  public async getAllUsers() {
    this.usersLoading = true;
    (await this._apiClientService.readAllApiService(`admin/itt-user/login/history?page=${this.currentPage}&pageSize=${this.pageSize}`)).subscribe(
      (event: HttpEvent<any>): any => {
        switch (event.type) {
          case HttpEventType.Sent:
            this.usersLoading = true;
            break;
          case HttpEventType.Response: {
            this.usersLoading = false;
            const { data, count } = event.body || {};
            this.totalItems = Number(count || 0);
            this.users = Array.isArray(data) ? data : [];
            break;
          }
        }
      },
      (errorObj: any) => {
        this.usersLoading = false;
        const errorMsg = errorObj?.error?.Message || errorObj?.error?.message;
        this.hasError = true;
        this.errorMsg = errorMsg || 'Something went wrong. Please contact Technical Support.';
      },
    );
  }

  public async getDashboardData(days = this.dashboardRangeDays) {
    this.dashboardLoading = true;
    this.hasError = false;
    this.errorMsg = '';
    this.dashboardRangeDays = days;

    (await this._apiClientService.displayDashboard(`admin/dashboard?days=${days}`)).subscribe(
      (event: HttpEvent<DashboardAnalytics>): any => {
        switch (event.type) {
          case HttpEventType.Sent:
            this.dashboardLoading = true;
            break;
          case HttpEventType.Response:
            this.dashboardLoading = false;
            this.applyDashboardData(event.body);
            break;
        }
      },
      (errorObj: any) => {
        this.dashboardLoading = false;
        const errorMsg = errorObj?.error?.Message || errorObj?.error?.message || errorObj?.error?.error;
        this.hasError = true;
        this.errorMsg = errorMsg || 'Something went wrong. Please contact Technical Support.';
        this._toastrService.error(this.errorMsg, 'Dashboard data failed');
      },
    );
  }

  public renderPage(event: number) {
    window.scroll(0, 0);
    this.currentPage = event;
    this.getAllUsers();
  }

  public formatMoney(amount: number, currency: string): string {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        maximumFractionDigits: 2,
      }).format(amount || 0);
    } catch {
      return `${currency} ${new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(amount || 0)}`;
    }
  }

  public trackByThemeCurrency(_: number, row: RevenueByTheme): string {
    return `${row.themeId}-${row.currency}`;
  }

  public trackByTheme(_: number, row: ActiveTagsByTheme): string {
    return row.themeId;
  }

  private applyDashboardData(data: DashboardAnalytics | null) {
    if (!data?.totals) {
      this.setEmptyCharts();
      return;
    }

    this.totalUser = Number(data.totals.users || 0);
    this.totalDevices = Number(data.totals.devices || 0);
    this.totalSubscriptions = Number(data.totals.subscriptions?.total || 0);
    this.totalUserThemes = Number(data.totals.themes || 0);
    this.totalSubscribedUsers = Number(data.totals.subscribedUsers || 0);
    this.subscriptionTotals = {
      free: Number(data.totals.subscriptions?.free || 0),
      paid: Number(data.totals.subscriptions?.paid || 0),
      unknownTheme: Number(data.totals.subscriptions?.unknownTheme || 0),
      total: Number(data.totals.subscriptions?.total || 0),
    };
    this.revenueByTheme = Array.isArray(data.revenueByTheme) ? data.revenueByTheme : [];
    this.activeTagsByTheme = Array.isArray(data.activeTagsByTheme) ? data.activeTagsByTheme : [];
    this.revenueTotalsByCurrency = Object.entries(data.revenueTotalsByCurrency || {}).map(([currency, amount]) => ({
      currency,
      amount: Number(amount || 0),
    }));

    this.dashboardRangeLabel = this.formatRangeLabel(data.range?.from, data.range?.to);
    this.setTrendChart(data);
    this.setSubscriptionChart();
    this.setActiveTagsChart();
  }

  private setEmptyCharts() {
    this.chartOptions = this.getTrendChartOptions([], [], []);
    this.chartOptionsDonut = this.getSubscriptionChartOptions([0, 0, 0]);
    this.chartOptionsLines = this.getActiveTagChartOptions([], []);
  }

  private setTrendChart(data: DashboardAnalytics) {
    const signups = this.fillDateSeries(data.signupsByDate || [], data.range?.from, data.range?.to);
    const themes = this.fillDateSeries(data.themesCreatedByDate || [], data.range?.from, data.range?.to);
    const labels = signups.map((point) => this.formatShortDate(point.date));

    this.chartOptions = this.getTrendChartOptions(
      labels,
      signups.map((point) => point.count),
      themes.map((point) => point.count),
    );
  }

  private setSubscriptionChart() {
    this.chartOptionsDonut = this.getSubscriptionChartOptions([
      this.subscriptionTotals.free,
      this.subscriptionTotals.paid,
      this.subscriptionTotals.unknownTheme,
    ]);
  }

  private setActiveTagsChart() {
    const topThemes = this.activeTagsByTheme.slice(0, 8);
    this.chartOptionsLines = this.getActiveTagChartOptions(
      topThemes.map((theme) => theme.themeName || '(unknown theme)'),
      topThemes.map((theme) => Number(theme.activeTagCount || 0)),
    );
  }

  private getTrendChartOptions(labels: string[], signups: number[], themes: number[]): ChartOptions {
    return {
      series: [
        {
          name: 'Signups',
          data: signups,
        },
        {
          name: 'Themes created',
          data: themes,
        },
      ],
      chart: {
        height: 390,
        type: 'line',
        fontFamily: 'Plus Jakarta Sans, sans-serif',
        parentHeightOffset: 0,
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      colors: [chartColors.signups, chartColors.themes],
      stroke: {
        curve: 'smooth',
        width: [3, 3],
        lineCap: 'round',
      },
      legend: {
        position: 'top',
        horizontalAlign: 'left',
        offsetY: 0,
        fontSize: '12px',
        markers: {
          shape: 'circle',
        },
        itemMargin: {
          horizontal: 12,
          vertical: 8,
        },
      },
      grid: {
        borderColor: '#e7ecf0',
        strokeDashArray: 4,
        padding: {
          top: 6,
          right: 18,
          bottom: 14,
          left: 8,
        },
      },
      markers: {
        size: 0,
        strokeWidth: 3,
        hover: {
          size: 6,
          sizeOffset: 2,
        },
      },
      xaxis: {
        categories: labels,
        tickAmount: Math.min(8, Math.max(labels.length - 1, 1)),
        tickPlacement: 'on',
        labels: {
          rotate: 0,
          hideOverlappingLabels: true,
          trim: false,
          minHeight: 34,
          maxHeight: 44,
          style: {
            colors: '#707a82',
            fontSize: '11px',
            fontWeight: 500,
          },
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
      yaxis: {
        min: 0,
        tickAmount: 4,
        decimalsInFloat: 0,
        forceNiceScale: true,
        labels: {
          style: {
            colors: '#707a82',
            fontSize: '11px',
          },
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        marker: {
          show: true,
        },
        x: {
          show: true,
        },
      },
      title: {
        text: undefined,
      },
    };
  }

  private getSubscriptionChartOptions(series: number[]): ChartOptionsDonut {
    return {
      series,
      chart: {
        type: 'donut',
        height: 280,
        fontFamily: 'Plus Jakarta Sans, sans-serif',
      },
      labels: ['Free themes', 'Paid themes', 'Unknown theme'],
      colors: [chartColors.free, chartColors.paid, chartColors.unknown],
      dataLabels: {
        enabled: false,
        dropShadow: {
          enabled: false,
        },
        style: {
          fontSize: '11px',
          fontWeight: 700,
        },
      },
      plotOptions: {
        pie: {
          customScale: 0.92,
          donut: {
            size: '66%',
            labels: {
              show: true,
              name: {
                show: true,
                color: '#707a82',
                fontSize: '12px',
                fontWeight: 600,
                offsetY: -4,
              },
              value: {
                show: true,
                color: '#111c2d',
                fontSize: '20px',
                fontWeight: 700,
                offsetY: 4,
                formatter: (value: string) => new Intl.NumberFormat('en-US').format(Number(value || 0)),
              },
              total: {
                show: true,
                showAlways: true,
                label: 'Total',
                color: '#707a82',
                fontSize: '12px',
                fontWeight: 600,
                formatter: () => new Intl.NumberFormat('en-US').format(this.subscriptionTotals.total || 0),
              },
            },
          },
        },
      },
      legend: {
        show: false,
      },
      tooltip: {
        y: {
          formatter: (value: number) => `${new Intl.NumberFormat('en-US').format(value)} subscriptions`,
        },
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              width: 260,
            },
            plotOptions: {
              pie: {
                customScale: 0.86,
              },
            },
          },
        },
      ],
    };
  }

  private getActiveTagChartOptions(labels: string[], values: number[]): ChartOptionsBars {
    return {
      series: [
        {
          name: 'Active tags',
          data: values,
        },
      ],
      chart: {
        height: 300,
        type: 'bar',
        toolbar: {
          show: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      fill: {
        opacity: 1,
      },
      markers: {
        size: 0,
      },
      xaxis: {
        categories: labels,
        labels: {
          rotate: -35,
          trim: true,
        },
      },
      yaxis: {
        min: 0,
        forceNiceScale: true,
      },
      tooltip: {
        y: {
          formatter: (value: number) => `${new Intl.NumberFormat('en-US').format(value)} active tags`,
        },
      },
    };
  }

  private fillDateSeries(records: DateCount[], from?: string, to?: string): DateCount[] {
    if (!from || !to) {
      return records;
    }

    const counts = new Map(records.map((record) => [record.date, Number(record.count || 0)]));
    const output: DateCount[] = [];
    const cursor = this.toUtcDateOnly(from);
    const end = this.toUtcDateOnly(to);

    while (cursor.getTime() <= end.getTime()) {
      const date = cursor.toISOString().slice(0, 10);
      output.push({
        date,
        count: counts.get(date) || 0,
      });
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }

    return output;
  }

  private toUtcDateOnly(value: string): Date {
    const date = new Date(value);
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  }

  private formatShortDate(date: string): string {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(`${date}T00:00:00.000Z`));
  }

  private formatRangeLabel(from?: string, to?: string): string {
    if (!from || !to) {
      return '';
    }

    const formatter = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });

    return `${formatter.format(new Date(from))} - ${formatter.format(new Date(to))}`;
  }
}
